import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

/**
 * NDVITimeseriesChart
 *
 * Pure SVG line chart showing NDVI value progression over time per field.
 * Includes health-zone bands (red/orange/yellow/green) and interactive tooltips.
 */

const FIELD_COLORS = [
  "#059669", // emerald-600
  "#2563eb", // blue-600
  "#d97706", // amber-600
  "#dc2626", // red-600
  "#7c3aed", // violet-600
  "#0891b2", // cyan-600
];

const HEALTH_ZONES = [
  { min: 0.0, max: 0.15, color: "#fecaca", label: "Bare / Stressed" },
  { min: 0.15, max: 0.3, color: "#fed7aa", label: "Low Vegetation" },
  { min: 0.3, max: 0.5, color: "#fef08a", label: "Moderate" },
  { min: 0.5, max: 0.7, color: "#bbf7d0", label: "Healthy" },
  { min: 0.7, max: 1.0, color: "#86efac", label: "Very Healthy" },
];

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  } catch {
    return iso;
  }
};

const NDVITimeseriesChart = ({ ndviTimeseries, isArabic }) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [ref, inView] = useInView({ triggerOnce: true, rootMargin: "-40px" });

  const SVG_W = 800;
  const SVG_H = 320;
  const PAD_L = 55;
  const PAD_R = 20;
  const PAD_T = 20;
  const PAD_B = 50;
  const plotW = SVG_W - PAD_L - PAD_R;
  const plotH = SVG_H - PAD_T - PAD_B;

  const { allDates, minDate, maxDate, dateRange } = useMemo(() => {
    const dates = ndviTimeseries.flatMap((s) => s.data.map((p) => new Date(p.date).getTime()));
    const mn = Math.min(...dates);
    const mx = Math.max(...dates);
    return { allDates: dates, minDate: mn, maxDate: mx, dateRange: mx - mn || 1 };
  }, [ndviTimeseries]);

  const toX = (t) => PAD_L + ((t - minDate) / dateRange) * plotW;
  const toY = (ndvi) => PAD_T + plotH - (Math.max(0, Math.min(1, ndvi)) / 1.0) * plotH;

  const yTicks = [0, 0.2, 0.4, 0.6, 0.8, 1.0];

  // Generate evenly spaced x-axis labels
  const xLabels = useMemo(() => {
    const count = Math.min(6, ndviTimeseries[0]?.data.length || 0);
    if (count < 2) return [];
    const step = Math.floor((ndviTimeseries[0]?.data.length - 1) / (count - 1));
    return Array.from({ length: count }, (_, i) => {
      const idx = Math.min(i * step, (ndviTimeseries[0]?.data.length || 1) - 1);
      const point = ndviTimeseries[0]?.data[idx];
      if (!point) return null;
      const t = new Date(point.date).getTime();
      return { x: toX(t), label: formatDate(point.date) };
    }).filter(Boolean);
  }, [ndviTimeseries, toX, minDate, dateRange]);

  if (!ndviTimeseries || ndviTimeseries.length === 0) return null;

  return (
    <div ref={ref} className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-sm">
      <div className={`flex items-center justify-between mb-5 flex-wrap gap-3 ${isArabic ? "flex-row-reverse" : ""}`}>
        <div className={`flex items-center gap-2 ${isArabic ? "flex-row-reverse" : ""}`}>
          <span className="text-lg">🛰️</span>
          <h3 className="text-base font-bold text-slate-900 mb-0">
            {isArabic ? "تطور مؤشر NDVI عبر الزمن" : "NDVI Timeseries Analysis"}
          </h3>
        </div>
        <span className="text-xs text-slate-400 font-medium">
          {isArabic ? "مؤشر الغطاء النباتي المعياري" : "Normalized Difference Vegetation Index"}
        </span>
      </div>

      <div className="w-full overflow-x-auto">
        <svg
          viewBox={`0 0 ${SVG_W} ${SVG_H}`}
          className="w-full min-w-[500px]"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Health zone bands */}
          {HEALTH_ZONES.map((zone) => (
            <rect
              key={zone.label}
              x={PAD_L}
              y={toY(zone.max)}
              width={plotW}
              height={toY(zone.min) - toY(zone.max)}
              fill={zone.color}
              opacity={0.25}
            />
          ))}

          {/* Y-axis grid lines */}
          {yTicks.map((tick) => (
            <g key={tick}>
              <line
                x1={PAD_L}
                x2={SVG_W - PAD_R}
                y1={toY(tick)}
                y2={toY(tick)}
                stroke="#e2e8f0"
                strokeDasharray="4 4"
                strokeWidth={0.5}
              />
              <text
                x={PAD_L - 8}
                y={toY(tick) + 4}
                textAnchor="end"
                fill="#94a3b8"
                fontSize={10}
                fontFamily="monospace"
              >
                {tick.toFixed(1)}
              </text>
            </g>
          ))}

          {/* X-axis labels */}
          {xLabels.map((xl, i) => (
            <text
              key={i}
              x={xl.x}
              y={SVG_H - 8}
              textAnchor="middle"
              fill="#94a3b8"
              fontSize={10}
            >
              {xl.label}
            </text>
          ))}

          {/* Series lines */}
          {ndviTimeseries.map((series, si) => {
            const color = FIELD_COLORS[si % FIELD_COLORS.length];
            const points = series.data.map((p) => ({
              x: toX(new Date(p.date).getTime()),
              y: toY(p.ndvi),
              ndvi: p.ndvi,
              date: p.date,
              field: series.fieldTitle,
            }));

            if (points.length === 0) return null;

            const d = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

            // Area fill
            const areaD = `${d} L ${points[points.length - 1].x} ${toY(0)} L ${points[0].x} ${toY(0)} Z`;

            return (
              <g key={series.fieldId}>
                {inView && (
                  <>
                    <path d={areaD} fill={color} opacity={0.06} />
                    <motion.path
                      d={d}
                      fill="none"
                      stroke={color}
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 1.5, delay: si * 0.3, ease: "easeInOut" }}
                    />
                  </>
                )}
                {points.map((p, i) => (
                  <circle
                    key={i}
                    cx={p.x}
                    cy={p.y}
                    r={hoveredPoint?.date === p.date && hoveredPoint?.field === p.field ? 6 : 3.5}
                    fill="white"
                    stroke={color}
                    strokeWidth={2}
                    className="cursor-pointer transition-all"
                    onMouseEnter={() => setHoveredPoint(p)}
                    onMouseLeave={() => setHoveredPoint(null)}
                  />
                ))}
              </g>
            );
          })}

          {/* Tooltip */}
          {hoveredPoint && (
            <g>
              <rect
                x={Math.min(hoveredPoint.x - 50, SVG_W - PAD_R - 110)}
                y={hoveredPoint.y - 42}
                width={100}
                height={34}
                rx={6}
                fill="#0f172a"
                opacity={0.9}
              />
              <text
                x={Math.min(hoveredPoint.x, SVG_W - PAD_R - 60)}
                y={hoveredPoint.y - 26}
                textAnchor="middle"
                fill="white"
                fontSize={10}
                fontWeight="bold"
              >
                NDVI: {hoveredPoint.ndvi.toFixed(3)}
              </text>
              <text
                x={Math.min(hoveredPoint.x, SVG_W - PAD_R - 60)}
                y={hoveredPoint.y - 14}
                textAnchor="middle"
                fill="#94a3b8"
                fontSize={9}
              >
                {formatDate(hoveredPoint.date)}
              </text>
            </g>
          )}
        </svg>
      </div>

      {/* Legend */}
      <div className={`flex flex-wrap gap-4 mt-4 ${isArabic ? "flex-row-reverse" : ""}`}>
        {ndviTimeseries.map((series, si) => (
          <div key={series.fieldId} className={`flex items-center gap-1.5 ${isArabic ? "flex-row-reverse" : ""}`}>
            <span
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: FIELD_COLORS[si % FIELD_COLORS.length] }}
            />
            <span className="text-xs text-slate-700 font-medium">{series.fieldTitle}</span>
            {series.cropName && (
              <span className="text-[10px] text-slate-400 border border-slate-200 rounded-full px-2 py-0.5">
                {series.cropName}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Health zone legend */}
      <div className={`flex flex-wrap gap-2 mt-3 ${isArabic ? "flex-row-reverse" : ""}`}>
        {HEALTH_ZONES.map((zone) => (
          <div key={zone.label} className={`flex items-center gap-1 ${isArabic ? "flex-row-reverse" : ""}`}>
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: zone.color }} />
            <span className="text-[10px] text-slate-400">{zone.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NDVITimeseriesChart;
